import { 
  LeadConversionRequest, 
  LeadConversionReport,
  AgentPerformanceRequest,
  AgentPerformanceReport,
  ConversationAnalyticsRequest,
  ConversationAnalyticsReport,
  UserActivityRequest,
  UserActivityReport,
  FilterOptions
} from '../types/reports';

const API_BASE_URL = process.env.REACT_APP_API_URL;

class ReportsApi {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('authToken');
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async getLeadConversionReport(params: LeadConversionRequest): Promise<LeadConversionReport> {
    const queryParams = new URLSearchParams();
    
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.agentId) queryParams.append('agentId', params.agentId);
    if (params.userId) queryParams.append('userId', params.userId);
    if (params.leadQuality) queryParams.append('leadQuality', params.leadQuality);
    if (params.status) queryParams.append('status', params.status);
    if (params.groupBy) queryParams.append('groupBy', params.groupBy);
    if (params.includeFollowUpStats !== undefined) queryParams.append('includeFollowUpStats', params.includeFollowUpStats.toString());
    if (params.includeQualityBreakdown !== undefined) queryParams.append('includeQualityBreakdown', params.includeQualityBreakdown.toString());

    return this.request<LeadConversionReport>(`/reports/GetLeadConversionReport?${queryParams.toString()}`);
  }

  async getAgentPerformanceReport(params: AgentPerformanceRequest): Promise<AgentPerformanceReport> {
    const queryParams = new URLSearchParams();
    
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.agentId) queryParams.append('agentId', params.agentId);
    if (params.agentStatus) queryParams.append('agentStatus', params.agentStatus);
    if (params.typeProduct) queryParams.append('typeProduct', params.typeProduct);
    if (params.groupBy) queryParams.append('groupBy', params.groupBy);
    if (params.includeResponseTime !== undefined) queryParams.append('includeResponseTime', params.includeResponseTime.toString());
    if (params.includeChatStats !== undefined) queryParams.append('includeChatStats', params.includeChatStats.toString());

    return this.request<AgentPerformanceReport>(`/reports/GetAgentPerformanceReport?${queryParams.toString()}`);
  }

  async getConversationAnalyticsReport(params: ConversationAnalyticsRequest): Promise<ConversationAnalyticsReport> {
    const queryParams = new URLSearchParams();
    
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.agentId) queryParams.append('agentId', params.agentId);
    if (params.userId) queryParams.append('userId', params.userId);
    if (params.conversationStatus) queryParams.append('conversationStatus', params.conversationStatus);
    if (params.groupBy) queryParams.append('groupBy', params.groupBy);
    if (params.includeMessageCount !== undefined) queryParams.append('includeMessageCount', params.includeMessageCount.toString());
    if (params.includeResponseTime !== undefined) queryParams.append('includeResponseTime', params.includeResponseTime.toString());
    if (params.includeStatusBreakdown !== undefined) queryParams.append('includeStatusBreakdown', params.includeStatusBreakdown.toString());

    return this.request<ConversationAnalyticsReport>(`/reports/GetConversationAnalyticsReport?${queryParams.toString()}`);
  }

  async getUserActivityReport(params: UserActivityRequest): Promise<UserActivityReport> {
    const queryParams = new URLSearchParams();
    
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.userId) queryParams.append('userId', params.userId);
    if (params.groupBy) queryParams.append('groupBy', params.groupBy);
    if (params.includeLeadAssignment !== undefined) queryParams.append('includeLeadAssignment', params.includeLeadAssignment.toString());
    if (params.includeConversationHandling !== undefined) queryParams.append('includeConversationHandling', params.includeConversationHandling.toString());

    return this.request<UserActivityReport>(`/reports/GetUserActivityReport?${queryParams.toString()}`);
  }

  async getAvailableReportTypes(): Promise<string[]> {
    return this.request<string[]>('/reports/GetAvailableReport');
  }

  async getFilterOptions(): Promise<FilterOptions> {
    return this.request<FilterOptions>('/reports/GetFilterOptions');
  }
}

export const reportsApi = new ReportsApi();
